import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, Sparkles, ShieldAlert } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useLocalization } from '@/hooks/useLocalization';

interface Suggestion {
  action: string;
  rationale: string;
  sourceSignal: { rule_id: string; entity_type: string; entity_id: string };
}

interface MitigationResult {
  suggestions: Suggestion[];
  insufficientEvidence: boolean;
  generatedAt: string;
}

export const RiskMitigationPanel = ({ projectId }: { projectId: string }) => {
  const { t } = useLocalization();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<MitigationResult | null>(null);

  // generate-risk-mitigation-v2 persists every run to ai_outputs
  // (output_type='risk_mitigation', metadata.project_id). Loading the most
  // recent row here — instead of only holding the last run in local state —
  // is what makes suggestions survive a tab switch (Radix unmounts inactive
  // tab content) or navigating back to this project later.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setResult(null);

    supabase
      .from('ai_outputs')
      .select('structured_content, created_at')
      .eq('output_type', 'risk_mitigation')
      .eq('metadata->>project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        setLoading(false);
        if (error) {
          console.error('Failed to load persisted mitigation suggestions:', error.message);
          return;
        }
        if (data?.structured_content) {
          setResult({
            ...(data.structured_content as Omit<MitigationResult, 'generatedAt'>),
            generatedAt: data.created_at,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const generate = async () => {
    setLoading(true);
    setResult(null);
    const { data, error } = await supabase.functions.invoke('generate-risk-mitigation-v2', {
      body: { projectId },
    });
    setLoading(false);
    if (error) {
      toast.error(t('Failed to generate mitigation suggestions. Please try again.'));
      return;
    }
    setResult(data as MitigationResult);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-sm text-muted-foreground">{t('Analyzing open risk signals and generating recommendations...')}</p>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="p-4 rounded-full bg-primary/10 mb-4">
          <Shield className="h-8 w-8 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-2">{t('Risk Mitigation Suggestions')}</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          
          {t('Generate concrete mitigation suggestions grounded in this project\'s currently open risk signals.')}
        </p>
        <Button onClick={generate} className="gap-2">
          <Sparkles className="h-4 w-4" />
          
          {t('Generate Recommendations')}
        </Button>
      </div>
    );
  }

  if (result.insufficientEvidence || result.suggestions.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
          <ShieldAlert className="h-8 w-8 mb-3 opacity-40" />
          <p className="text-sm font-medium text-foreground">{t('No mitigation suggestions available')}</p>
          <p className="text-xs mt-1 max-w-md">
            
            {t('This project currently has no open risk signals, or the available evidence is too thin to responsibly suggest anything.')}
          </p>
        </div>
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={generate} className="gap-1">
            <Sparkles className="h-3 w-3" />  {t('Check again')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{t('Suggested Actions')}</h4>
        <Button variant="outline" size="sm" onClick={generate} className="gap-1">
          <Sparkles className="h-3 w-3" />  {t('Regenerate')}
        </Button>
      </div>
      <div className="space-y-3">
        {result.suggestions.map((s, idx) => (
          <Card key={idx} className="bg-card">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-1">{s.action}</p>
              <p className="text-sm text-muted-foreground mb-3">{s.rationale}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className="text-xs capitalize">{s.sourceSignal.entity_type}</Badge>
                <span>{s.sourceSignal.entity_id}</span>
                <span>&middot;</span>
                <span>{s.sourceSignal.rule_id.replace(/_/g, ' ')}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RiskMitigationPanel;
